import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield,
  MessageCircle,
  Pill,
  Heart,
  Users,
  Lock,
  FileSearch,
  Phone,
  Brain,
  Trash2,
  KeyRound,
  Server,
  FileKey,
  ShieldCheck,
  Database,
  Sparkles,
  Stethoscope,
  Mail,
} from 'lucide-react';

import SEO from '../components/SEO';

const PublicAboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        >
          ← Retour
        </button>
      </div>
      <SEO
        title="Qui sommes-nous"
        description="Remadia : une IA médicale bienveillante pour comprendre votre santé."
        path="/about"
      />
      
      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="about-page">
        
        {/* Header Hero */}
        <div className="text-center mb-20">
          <img src="/RemaH.png" alt="Remadia AI" className="w-64 h-auto mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-gray-100 mb-6 tracking-tight">
            La clarté médicale, <span className="text-blue-600">enfin accessible.</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Parce que comprendre sa santé ne devrait pas être un parcours du combattant, 
            nous avons créé Remadia.
          </p>
        </div>

        <div className="space-y-20 text-gray-700 dark:text-gray-300">
          
          {/* Notre vision */}
          <section className="relative">
            <div className="flex items-center gap-3 mb-6">
              <span className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Heart className="w-6 h-6 text-red-500" />
              </span>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Pourquoi Remadia ?</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <p className="text-lg">
                  Aujourd'hui, une recherche Google sur un symptôme finit souvent par nous inquiéter inutilement.
                </p>
                <p>
                  Notre vision est de transformer cette anxiété en <span className="font-semibold text-blue-600">sérénité</span>. 
                  Remadia n'est pas juste un outil, c'est un traducteur qui simplifie le jargon médical pour vous redonner le contrôle.
                </p>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-400 rounded-r-lg">
                   <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                     Indépendance garantie : 100% de nos revenus proviennent de nos abonnés. Vos données ne sont pas notre produit.
                   </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl p-8 text-white shadow-xl">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" /> Notre Mission
                </h3>
                <p className="text-blue-50 leading-relaxed">
                  "Accompagner chaque famille vers une meilleure compréhension de sa santé, 
                  en rendant l'information médicale aussi claire qu'une discussion avec un ami bienveillant."
                </p>
              </div>
            </div>
          </section>

          {/* Chiffres clés */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 shadow-xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
              <div className="space-y-1">
                <div className="text-2xl">🕐</div>
                <p className="font-bold text-lg leading-tight">Disponible 24h/24</p>
                <p className="text-blue-100 text-sm">7j/7</p>
              </div>
              <div className="space-y-1">
                <div className="text-2xl">🇪🇺</div>
                <p className="font-bold text-lg leading-tight">Données hébergées</p>
                <p className="text-blue-100 text-sm">en Europe</p>
              </div>
              <div className="space-y-1">
                <div className="text-2xl">🚫</div>
                <p className="font-bold text-lg leading-tight">0 publicité</p>
                <p className="text-blue-100 text-sm">jamais</p>
              </div>
              <div className="space-y-1">
                <div className="text-2xl">✅</div>
                <p className="font-bold text-lg leading-tight">Conforme RGPD</p>
                <p className="text-blue-100 text-sm">100%</p>
              </div>
            </div>
          </section>

          {/* Roadmap */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Ce qui arrive bientôt</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <span className="text-2xl">💊</span>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">Pilulier intelligent</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Gestion et rappels de médicaments</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded-full whitespace-nowrap">Bientôt</span>
              </div>
              <div className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <span className="text-2xl">📱</span>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">Application mobile Android</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Toutes les fonctionnalités sur votre smartphone</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded-full whitespace-nowrap">Bientôt</span>
              </div>
              <div className="flex items-center justify-between p-5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4">
                  <span className="text-2xl">📱</span>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">Application mobile iOS</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">En cours de développement</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded-full whitespace-nowrap">Bientôt</span>
              </div>
            </div>
          </section>

          {/* Nous contacter */}
          <section className="p-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <span className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </span>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Une question ? Un problème ?</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-5">
              Notre équipe est là pour vous aider. Contactez-nous par email ou échangez avec la communauté sur le forum.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:support@remadia.com"
                className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                support@remadia.com
              </a>
              <Link
                to="/forum"
                className="flex items-center gap-2 px-5 py-3 border-2 border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors text-sm"
              >
                Accéder au forum communautaire →
              </Link>
            </div>
          </section>

          {/* Ce que propose Remadia (Grille plus "App-like") */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center">Ce que vous pouvez faire</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: <MessageCircle className="text-blue-500" />,
                  title: "Chat santé bienveillant",
                  desc: "Discutez sans tabou. L'IA s'adapte à votre niveau de connaissance pour expliquer simplement."
                },
                {
                  icon: <FileSearch className="text-rose-500" />,
                  title: "Décodeur de bilans",
                  desc: "Transformez vos chiffres complexes en explications colorées et imagées (vert, orange, violet)."
                },
                {
                  icon: <Pill className="text-emerald-500" />,
                  title: "Guide des médicaments",
                  desc: "Comprenez mieux vos traitements, leurs effets et leur mode d'action en un clin d'œil."
                },
                {
                  icon: <Brain className="text-indigo-500" />,
                  title: "Quiz & Plans d'action",
                  desc: "Évaluez vos priorités et recevez des conseils personnalisés pour améliorer votre quotidien."
                }
              ].map((item, idx) => (
                <div key={idx} className="group p-6 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm hover:shadow-md transition-all">
                  <div className="mb-4 p-2 bg-gray-50 dark:bg-gray-700 w-fit rounded-xl group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Sécurité (Style "Coffre-fort") */}
          <section className="bg-gray-900 text-gray-100 rounded-3xl p-8 md:p-12 overflow-hidden relative shadow-2xl">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <Lock className="w-8 h-8 text-amber-400" />
                <h2 className="text-3xl font-bold text-white">Votre vie privée est sacrée</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <ShieldCheck className="w-6 h-6 text-blue-400 shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Chiffrement AES-256-GCM</h4>
                      <p className="text-sm text-gray-400">Vos données sont chiffrées avec une clé propre à votre compte.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Server className="w-6 h-6 text-purple-400 shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Hébergement Européen</h4>
                      <p className="text-sm text-gray-400">Vos informations ne quittent jamais le sol européen, protégé par le RGPD.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                  <p className="italic text-gray-300 text-sm leading-relaxed">
                    "Chez Remadia, nous ne stockons pas vos données pour les analyser. Nous les chiffrons avec une clé propre à votre compte pour les protéger."
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Pour qui */}
          <section className="text-center">
            <h2 className="text-3xl font-bold mb-10">Une solution pour tous</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-3">
                <div className="text-4xl">🩺</div>
                <h3 className="font-bold">Professionnels</h3>
                <p className="text-sm text-gray-500">Un appui pour mieux expliquer les pathologies à vos patients.</p>
              </div>
              <div className="space-y-3">
                <div className="text-4xl">💜</div>
                <h3 className="font-bold">Aidants</h3>
                <p className="text-sm text-gray-500">Le soutien nécessaire pour accompagner vos proches sereinement.</p>
              </div>
              <div className="space-y-3">
                <div className="text-4xl">👨‍👩‍👧‍👦</div>
                <h3 className="font-bold">Familles</h3>
                <p className="text-sm text-gray-500">Du nouveau-né aux seniors, des conseils adaptés à chaque étape.</p>
              </div>
            </div>
          </section>

          {/* Vandomia */}
          <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 md:p-12 shadow-2xl text-white">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8">
              <div className="flex-1 space-y-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">Derrière Remadia</p>
                <h2 className="text-3xl font-extrabold text-white">Vandomia</h2>
                <p className="text-gray-300 leading-relaxed">
                  Remadia Health et Remadia Pro sont des produits conçus et développés par{' '}
                  <a
                    href="https://vandomia.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 font-semibold underline underline-offset-2 transition-colors"
                  >
                    Vandomia
                  </a>
                  , une société française spécialisée dans la création de solutions numériques innovantes pour la santé et le bien-être.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Notre mission est de concevoir des expériences digitales qui mettent réellement la technologie au service de l'humain — aussi bien pour le grand public que pour les professionnels de santé.
                </p>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="https://vandomia.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5"
                >
                  Découvrir Vandomia →
                </a>
              </div>
            </div>
          </section>

          {/* Avertissement (Plus doux) */}
          <section className="p-6 bg-amber-50 dark:bg-amber-950/20 rounded-2xl border-2 border-dashed border-amber-200 dark:border-amber-800/50">
            <div className="flex items-center gap-2 mb-3 text-amber-800 dark:text-amber-300">
              <Stethoscope className="w-5 h-5" />
              <h2 className="font-bold uppercase tracking-wider text-sm">Rappel important</h2>
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed">
              Remadia est votre compagnon d'information, pas votre médecin. Nous ne posons pas de diagnostic et ne prescrivons aucun traitement. En cas d'urgence, contactez immédiatement le <strong>15 (SAMU)</strong> ou le <strong>112</strong>.
            </p>
          </section>
         

        </div>
      </div>
    </div>
  );
};

export default PublicAboutPage;